import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Header } from "../../../components/header/Header";
import { createSelector } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { 
  getAllContestThunk as onGetAllContestThunk,
  createContestThunk as onCreateContest,
  resetContestThunk as onResetContest,
  createParticipateContestThunk as onCreateParticipateContestThunk
} from "../../../slices/contest/thunk";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getRole } from "../../../utils/GetRole";
import toast from "react-hot-toast";
import { getAuthUser, getFileUrl } from "../../../utils/GetAuthUser";
import { Spinner } from "../../../components/spinner/Spinner";

const defaultImage = "/default.png";

export const YourContests = () => {

  const fileUrl =getFileUrl();
  const role = useMemo(() => getRole(), []);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { statusCode } = useParams();
  const authUser = useMemo(() => getAuthUser(), []);
  const [loadingContestId, setLoadingContestId] = useState(null);
  const [isContestSave, setIsContestSave] = useState(false);
  const [filteredContests, setFilteredContests] = useState([]);

  // Redux selector
  const selectPlansState = (state) => state.contest;
  const contestSelector = createSelector(selectPlansState, (contest) => ({
    isLoading: contest.isLoading,
    contestList: contest.contestList,
    isCreateContest: contest.isCreateContest,
  }));

  var { contestList, isCreateContest, isLoading } = useSelector(contestSelector);

  // Fetch contests only when component mounts or statusCode changes
  const fetchContests = useCallback(async () => {
    const contestData = {
      statusCode: 0,
      artistId: authUser?.user?.id || null,
      is_publish: 1,
      checkParticipatArtistId: authUser?.user?.id || null,
    };
    
    dispatch(onResetContest());

    //we will fetch all contest then we will check participant status
   var res = await dispatch(onGetAllContestThunk(contestData)).unwrap();

   contestList = res?.data || [];

   // we need to filter out the contest list and show only those contest in which participants array  have artist_id with is_accept 1
    contestList = contestList.filter(contest => {
      if(contest.participants && contest.participants.length > 0)
      {
        
        const participant = contest.participants.find(p => p.artist_id === authUser?.user?.id && p.is_accept == "1");
        return participant !== undefined;
      }
    });
  //  console.log("filtered contests-------:", contestList);

   setFilteredContests(contestList);



  }, [dispatch, statusCode, authUser]);

 

  // Reset contest creation flag when component mounts to prevent duplicate toasts
  useEffect(() => {
    dispatch(onResetContest());
  }, [dispatch]);

  useEffect(() => {
    fetchContests();
  }, [fetchContests]);

  // Navigate to sketch details - memoized to prevent recreation on each render
  const handleNavigateToSketchDetail = useCallback((contestId) => {
    navigate(`/${role}/contest-detail/2/${contestId}`);
  }, [navigate, role]);

  // Create contest handler - memoized
  const handleCreateContest = useCallback((contestId) => {
    setLoadingContestId(contestId);
    dispatch(onCreateContest({'contestId': contestId }));
  }, [dispatch]);


  // Participate handler - memoized
  const handleParticipate = useCallback((contestId) => {
    
    dispatch(onCreateParticipateContestThunk({ 
      'contestId': contestId,
      'artistId': authUser?.user?.id || null,
      'is_accept':1
    }));
  }, [dispatch, authUser]);

  // Handle contest creation success or error
  useEffect(() => {
    if (isCreateContest) {
    setIsContestSave(true);
    }
  }, [isCreateContest]);

  //isContestSave
    useEffect(() => {
    if (isContestSave) {
      toast.success('Contest created successfully');
      setLoadingContestId(null);
      fetchContests();
    }
    setIsContestSave(false)
  }, [isContestSave,fetchContests]);
  
  // Reset loading state when API call completes (success or error)
  useEffect(() => {
    if (!isLoading && loadingContestId) {
      setLoadingContestId(null);
    }
  }, [isLoading]);




  // Contest card component for better readability
  const ContestCard = useCallback(({ card }) => {
    const contestImage = card.contestImage
      ? `${fileUrl}${card.contestImage}`
      : defaultImage;

     return (
        <div
          key={card.id}
          className="flex flex-col h-full justify-between bg-transparent overflow-hidden p-3 border border-border_color rounded-xl relative"
        >
          {/* Contest Image */}
          <div className="w-full h-[175px] rounded-2xl overflow-hidden">
            <img
              alt={card.title || "contest image"}
              className="object-cover object-center w-full h-full block"
              src={contestImage}
              onError={(e) => (e.currentTarget.src = defaultImage)}
            />
          </div>
          <span className="text-sm text-black font-medium font-grotesk absolute top-4 right-4 bg-white px-2 py-1 rounded-full shadow-md">
            {card.status}
          </span>
          <div className="flex flex-col justify-between flex-1 mt-3">
            <div className="py-3 flex flex-col flex-grow justify-between">
              {/* Category */}
              <div className="flex items-center justify-center border border-[#DDDDDD] py-[6px] px-[14px] w-[130px] rounded-3xl ">
                <span className="font-bold text-[11px] text-[#262626] font-grotesk text-center">
                  {card.title || card.contestCategory}
                </span>
              </div>

              {/* Description */}
              <div className="mt-3 text-start">
                <p className="text-[#696565] text-sm font-grotesk font-medium line-clamp-2">
                  {card.description || card.contestDescription}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-2 w-full mt-4 flex-wrap">
                <Link
                  className="w-full flex flex-1 items-center justify-center text-xs font-bold border border-border_color font-grotesk text-primary py-3 px-5 rounded-[10px] whitespace-nowrap tracking-wide hover:text-white hover:bg-primary transition-all delay-75"
                  to={`/artist/design-detail/${card.id}`}
                >
                  View Details
                </Link>

                {card.status == "Created" && card.started_at == null && (
                  <button
                    className="w-full flex flex-1 items-center justify-center text-xs font-bold text-white bg-primary font-grotesk text-center tracking-wide   py-3 px-5 rounded-[10px] whitespace-nowrap"
                    onClick={() => handleParticipate(card.id)}
                  >
                    Start Designing
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
  }, [fileUrl, statusCode, handleNavigateToSketchDetail, handleCreateContest, handleParticipate]);

  return (
    <>
      {/* Header */}
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header />
      </header>
      
      <div className="w-full mx-auto bg-[#F7F7F7]">
        <div className="w-full">
          {/* Page Title */}
          <div className="w-full flex items-center justify-start mt-6">
            <span className="font-bold text-black text-[22px] font-grotesk">
              Your Contest
            </span>
          </div>

          {/* Contest Grid */}
          <div className="w-full mt-6">
            <div className="w-full mx-auto grid gap-7 grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
              {filteredContests && filteredContests.length > 0 ? (
                filteredContests.map((card) => (
                  <ContestCard key={card.id} card={card} />
                ))
              ) : (
                <p className="text-center text-gray-500">No contests found</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
